import React from 'react';

import ErrorBoundary from './ErrorBoundary';

/**
 * withErrorBoundary - Higher-Order Component for wrapping components with ErrorBoundary
 *
 * Provides a convenient way to wrap components with error handling without JSX.
 *
 * @param {React.ComponentType} Component - The component to wrap
 * @param {Object} errorBoundaryProps - Props to pass to ErrorBoundary
 * @param {React.ReactNode|Function} errorBoundaryProps.fallback - Custom fallback UI
 * @param {Function} errorBoundaryProps.onError - Callback when error is caught
 * @param {Function} errorBoundaryProps.onReset - Callback when error is reset
 * @returns {React.ComponentType} - Wrapped component with error boundary
 *
 * @example
 * // Basic usage
 * const SafeComponent = withErrorBoundary(MyComponent);
 *
 * @example
 * // With custom fallback
 * const SafeComponent = withErrorBoundary(MyComponent, {
 *   fallback: <CustomErrorUI />,
 * });
 *
 * @example
 * // With callbacks
 * const SafeComponent = withErrorBoundary(MyComponent, {
 *   onError: (error, errorInfo) => {
 *     console.log('Error caught:', error);
 *   },
 *   onReset: () => {
 *     console.log('Error reset');
 *   },
 * });
 */
function withErrorBoundary(Component, errorBoundaryProps = {}) {
  function Wrapped(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  }

  // Set display name for debugging
  const componentName = Component.displayName || Component.name || 'Component';
  Wrapped.displayName = `withErrorBoundary(${componentName})`;

  return Wrapped;
}

export default withErrorBoundary;
